// ============================================================================
// Role registry for Wantok Telehealth.
//
// These match the `user.role` strings issued by the Django backend (see
// core/api_auth.py::derive_role). The backend returns one of:
//   doctor | specialist | patient | lab_staff | pharmacy_staff |
//   radiology_staff | ambulance_staff | admin | org_admin | unknown
//
// Every role-specific route uses these constants so renames only touch one
// file. The ROLE_HOMES map feeds RoleHome.jsx; RequireRole guards use
// ROLE_GROUPS to permit related roles to share UI (e.g. specialist uses the
// doctor workspace until a specialist-specific feature ships).
// ============================================================================

export const ROLES = Object.freeze({
  DOCTOR: 'doctor',
  SPECIALIST: 'specialist',
  PATIENT: 'patient',
  LABORATORY: 'lab_staff',
  PHARMACY: 'pharmacy_staff',
  RADIOLOGY: 'radiology_staff',
  AMBULANCE: 'ambulance_staff',
  ADMIN: 'admin',
  // Subscribing-organization admin — HR / wellness lead at a customer
  // organization. NOT the same as platform admin; their landing page
  // is the workforce-health dashboard, not /admin-invitations.
  ORG_ADMIN: 'org_admin',
  // ICER-internal cost entry staff (Phase 6.4). Can record platform
  // operating costs + create cost categories. NO other admin powers —
  // no patient data, no audit log, no org dashboards. Lands on a
  // dedicated /cost-entry surface that ONLY exposes the platform-costs page.
  COST_ENTRY: 'cost_entry_staff',
})

// Grouping reused across route guards / feature flags.
export const ROLE_GROUPS = Object.freeze({
  CLINICIAN: [ROLES.DOCTOR, ROLES.SPECIALIST],
  PROVIDER: [ROLES.LABORATORY, ROLES.PHARMACY, ROLES.RADIOLOGY, ROLES.AMBULANCE],
  ADMIN: [ROLES.ADMIN],
})

// Landing route per role — consumed by routes/RoleHome.jsx.
export const ROLE_HOMES = Object.freeze({
  [ROLES.DOCTOR]: '/doctor',
  [ROLES.SPECIALIST]: '/doctor',
  [ROLES.PATIENT]: '/patient',
  [ROLES.LABORATORY]: '/lab',
  [ROLES.PHARMACY]: '/pharmacy',
  [ROLES.RADIOLOGY]: '/radiology',
  [ROLES.AMBULANCE]: '/ambulance',
  [ROLES.ADMIN]: '/admin-invitations',
  [ROLES.ORG_ADMIN]: '/org/dashboard',
  [ROLES.COST_ENTRY]: '/cost-entry/platform-costs',
})
